/**
 * Öffentliches Impressum (Fund 10, Jörg 2026-09-28). Ohne Login erreichbar,
 * Inhalt kommt von GET /api/impressum: Modus onvice (Betreiber onvice, Hetzner),
 * tenant (On-Prem mit eigenem Text) oder aus (geschlossenes System → Hinweis).
 * Dazu die Quellenhinweise (OSM-Adressdaten, Karten, Wetter) und der optionale
 * Link auf die Datenschutzhinweise des Mandanten.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

interface ImpressumData {
  mode: 'onvice' | 'tenant' | 'aus';
  tenantName: string | null;
  onvice: {
    anbieter: string; anschrift: string[]; vertretenDurch: string[]; telefon: string; email: string; homepage: string;
    registergericht: string; registernummer: string; ustId: string; kammer: string; verantwortlichInhalte: string; streitbeilegung: string;
  } | null;
  tenantText: string | null;
  quellen: Array<{ titel: string; text: string; url: string }>;
  datenschutzUrl: string | null;
}

export default function ImpressumPage() {
  const [data, setData] = useState<ImpressumData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/impressum', { credentials: 'omit' })
      .then(async (r) => {
        const j = await r.json().catch(() => null) as { success?: boolean; data?: ImpressumData; error?: string } | null;
        if (!r.ok || !j?.success || !j.data) throw new Error(j?.error ?? `HTTP ${r.status}`);
        return j.data;
      })
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e: Error) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground px-4 py-8">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Impressum</h1>
          <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground">Zum Login</Link>
        </div>

        {error && <div className="rounded-lg border border-default bg-muted p-4 text-sm">Impressum konnte nicht geladen werden: {error}</div>}
        {!data && !error && <div className="text-sm text-muted-foreground">Lädt …</div>}

        {data?.mode === 'aus' && (
          <div className="rounded-lg border border-default bg-muted p-4 text-sm text-muted-foreground" data-test-anchor="impressum-aus">
            Dieses Portal ist ein geschlossenes System{data.tenantName ? ` der ${data.tenantName}` : ''}, das nur im Firmennetz und über VPN erreichbar ist.
            Ein Impressum nach § 5 DDG ist dafür nicht vorgesehen. Fragen zum Betrieb beantwortet die Geschäftsführung.
          </div>
        )}

        {data?.mode === 'onvice' && data.onvice && (
          <section className="rounded-lg border border-default bg-muted p-5 space-y-4 text-sm" data-test-anchor="impressum-onvice">
            <p className="text-muted-foreground">Angaben gemäß § 5 DDG. Betreiber dieses Portals{data.tenantName ? ` für ${data.tenantName}` : ''}:</p>
            <Block titel="Diensteanbieter">
              <div>{data.onvice.anbieter}</div>
              {data.onvice.anschrift.map((z) => <div key={z}>{z}</div>)}
            </Block>
            <Block titel="Vertreten durch">{data.onvice.vertretenDurch.map((v) => <div key={v}>{v}</div>)}</Block>
            <Block titel="Kontakt">
              <div>Telefon: {data.onvice.telefon}</div>
              <div>E-Mail: <a className="underline" href={`mailto:${data.onvice.email}`}>{data.onvice.email}</a></div>
              <div>Homepage: <a className="underline" href={data.onvice.homepage} target="_blank" rel="noreferrer">{data.onvice.homepage.replace(/^https?:\/\//, '')}</a></div>
            </Block>
            <Block titel="Registereintrag">
              <div>Registergericht: {data.onvice.registergericht}</div>
              <div>Registernummer: {data.onvice.registernummer}</div>
            </Block>
            <Block titel="Umsatzsteuer-ID">
              <div>Umsatzsteuer-Identifikationsnummer gemäß § 27a Umsatzsteuergesetz: {data.onvice.ustId}</div>
            </Block>
            <Block titel="Zuständige Kammer">{data.onvice.kammer}</Block>
            <Block titel="Verantwortlich für redaktionelle Inhalte">{data.onvice.verantwortlichInhalte}</Block>
            <Block titel="Verbraucherstreitbeilegung">{data.onvice.streitbeilegung}</Block>
          </section>
        )}

        {data?.mode === 'tenant' && (
          <section className="rounded-lg border border-default bg-muted p-5 text-sm" data-test-anchor="impressum-tenant">
            {data.tenantText
              ? <div className="whitespace-pre-wrap">{data.tenantText}</div>
              : <div className="text-muted-foreground">Für diese Instanz ist noch kein Impressum hinterlegt (Verwaltung → Mandant).</div>}
          </section>
        )}

        {data && data.mode !== 'aus' && (
          <section className="rounded-lg border border-default bg-muted p-5 space-y-3 text-sm" data-test-anchor="impressum-quellen">
            <h2 className="font-semibold">Datenquellen und Lizenzen</h2>
            {data.quellen.map((q) => (
              <div key={q.titel}>
                <div className="font-medium">{q.titel}</div>
                <div className="text-muted-foreground">{q.text} — <a className="underline" href={q.url} target="_blank" rel="noreferrer">{q.url.replace(/^https?:\/\//, '')}</a></div>
              </div>
            ))}
          </section>
        )}

        {data?.datenschutzUrl && (
          <section className="rounded-lg border border-default bg-muted p-5 text-sm" data-test-anchor="impressum-datenschutz">
            <a className="underline" href={data.datenschutzUrl} target="_blank" rel="noreferrer">Datenschutzhinweise für Beschäftigte{data.tenantName ? ` (${data.tenantName})` : ''}</a>
          </section>
        )}
      </div>
    </div>
  );
}

function Block({ titel, children }: { titel: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{titel}</div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}
